import { ChangeDetectionStrategy, Component, DestroyRef, Inject, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import {
  FileSpecificationDeleteResult,
  FileSpecificationService,
} from 'src/app/services/file-specifications/file-specification.service';
import { FileSpecification } from 'src/app/models/file-specifications/file-specification';

export interface FileSpecificationDeleteDialogData {
  organizationId: string;
  current: FileSpecification;
}

interface DependentsWarning {
  count: number;
  names: string[];
}

@Component({
  selector: 'app-file-specification-delete-dialog',
  templateUrl: './file-specification-delete-dialog.component.html',
  styleUrls: ['./file-specification-delete-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class FileSpecificationDeleteDialogComponent {
  private readonly destroyRef = inject(DestroyRef);

  readonly acknowledgeControl = new FormControl<boolean>(false, { nonNullable: true });

  warning: DependentsWarning | null = null;
  probed = false;
  submitting = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FileSpecificationDeleteDialogData,
    private readonly dialogRef: MatDialogRef<FileSpecificationDeleteDialogComponent, boolean>,
    private readonly service: FileSpecificationService,
  ) {
    this.probeDependents();
  }

  get needsAcknowledge(): boolean {
    return this.warning !== null && this.warning.count > 0;
  }

  get canDelete(): boolean {
    if (this.submitting || !this.probed) return false;
    if (this.needsAcknowledge && !this.acknowledgeControl.value) return false;
    return true;
  }

  confirm(): void {
    if (!this.canDelete) return;
    this.submitting = true;
    this.service
      .remove(this.data.organizationId, this.data.current.id, true, this.mediaHint)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.dialogRef.close(true),
        error: () => {
          this.submitting = false;
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private probeDependents(): void {
    this.service
      .remove(this.data.organizationId, this.data.current.id, false, this.mediaHint)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: FileSpecificationDeleteResult) => {
          this.warning = result.warning;
          this.probed = true;
        },
        error: () => {
          this.probed = true;
        },
      });
  }

  private get mediaHint(): 'stills' | 'videos' {
    return this.data.current.value.settings.media;
  }
}
